import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

/**
 * Per-process AI provider + model picker.
 *
 * Lets an admin pick which provider+model handles a given process. When
 * the user changes either dropdown the new selection is persisted
 * immediately via PUT /admin/settings/process-ai-config/{processId}.
 */
const ProviderModelPicker = ({ processId, onChanged }) => {
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState(null); // { provider_id, model_id }
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);

  const loadModels = useCallback(async (providerId, providerList, currentModelId) => {
    try {
      const res = await adminApi.providerModels(providerId);
      const ms = res.data?.models || [];
      setModels(ms);
      // Sanity-check the current model still exists in the new list.
      if (currentModelId && !ms.find((m) => m.id === currentModelId)) {
        if (ms.length > 0) setConfig((c) => ({ ...c, model_id: ms[0].id }));
      }
      // If no model has ever been picked, surface the first option so the
      // dropdown isn't empty (we don't auto-persist — user has to confirm).
      if (!currentModelId && ms.length > 0) {
        setConfig((c) => ({ ...c, model_id: c.model_id || null }));
      }
    } catch {
      setModels([]);
    }
  }, []);

  // Load providers + per-process config
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, cfgRes] = await Promise.all([
        adminApi.listProviders(),
        adminApi.listProcessAIConfig(),
      ]);
      const enabled = (provRes.data?.providers || []).filter((p) => p.enabled);
      setProviders(enabled);
      const row = (cfgRes.data?.config || []).find((c) => c.process_id === processId);
      // Default to the system Anthropic provider if nothing configured.
      let providerId = row?.provider_id;
      let modelId = row?.model_id;
      if (!providerId) {
        const sys = enabled.find((p) => p.is_system);
        if (sys) providerId = sys.id;
      }
      setConfig({ provider_id: providerId, model_id: modelId });
      if (providerId) await loadModels(providerId, enabled, modelId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load AI config');
    } finally {
      setLoading(false);
    }
  }, [processId, loadModels]);

  useEffect(() => { load(); }, [load]);

  const onProviderChange = async (providerId) => {
    setConfig({ provider_id: providerId, model_id: null });
    setModels([]);
    await loadModels(providerId, providers, null);
  };

  const onModelChange = async (modelId) => {
    if (!config?.provider_id) return;
    try {
      await adminApi.updateProcessAIConfig(processId, {
        provider_id: config.provider_id,
        model_id: modelId,
      });
      setConfig((c) => ({ ...c, model_id: modelId }));
      toast.success('Provider/model updated');
      if (onChanged) onChanged({ provider_id: config.provider_id, model_id: modelId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update AI config');
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-rule p-4 text-xs text-muted-foreground">
        Loading AI configuration…
      </div>
    );
  }

  const currentProvider = providers.find((p) => p.id === config?.provider_id);

  return (
    <div
      className="rounded-lg border border-rule p-4 space-y-3"
      data-testid={`ai-config-${processId}`}
    >
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium text-foreground">AI provider & model</p>
        {currentProvider?.is_system && (
          <Badge variant="secondary" className="text-[10px]">Default</Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Pick the provider and model that handle this process. Changes apply on the next run —
        no deployment needed.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Provider</Label>
          <Select
            value={config?.provider_id || ''}
            onValueChange={onProviderChange}
          >
            <SelectTrigger data-testid={`ai-config-provider-${processId}`}>
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} <span className="text-muted-foreground ml-1">({p.type})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Model</Label>
          <Popover open={modelPickerOpen} onOpenChange={setModelPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={modelPickerOpen}
                disabled={!config?.provider_id || models.length === 0}
                data-testid={`ai-config-model-${processId}`}
                className="w-full justify-between font-normal"
              >
                <span className="truncate">
                  {models.find((m) => m.id === config?.model_id)?.display_name || 'Select model'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popper-anchor-width] p-0">
              <Command>
                <CommandInput placeholder="Search models by name, id, or provider…" />
                <CommandList>
                  <CommandEmpty>No model found.</CommandEmpty>
                  <CommandGroup>
                    {models.map((m) => (
                      <CommandItem
                        key={m.id}
                        value={`${m.display_name} ${m.id}`}
                        onSelect={() => {
                          onModelChange(m.id);
                          setModelPickerOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'shrink-0',
                            config?.model_id === m.id ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{m.display_name}</span>
                          {m.description && (
                            <span className="text-[11px] text-muted-foreground truncate">
                              {m.description}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {models.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              {models.length} model{models.length !== 1 ? 's' : ''} available
              {currentProvider?.type === 'openrouter' ? ' (live from OpenRouter)' : ''}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderModelPicker;
